import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import { strftime } from "../../../../utils/dates";
import { WAREHOUSE_LOCATION_AUDITS_QUERY } from "../../../../constants/graphql/queries";
import AuditDetailsModalSkeleton from "./AuditDetailsModalSkeleton";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 50;

const formatDateTime = value => {
  if (!value) return "-";
  return <Typography variant="body2">{strftime(value)}</Typography>;
};

const formatDuration = durationSeconds => {
  if (!durationSeconds && durationSeconds !== 0) return "-";
  const hours = Math.floor(durationSeconds / 3600);
  const minutes = Math.floor((durationSeconds % 3600) / 60);
  const seconds = durationSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

export default function AuditDetailsModal({ onClose, locationId, isMobileLocation }) {
  const [page, setPage] = useState(DEFAULT_PAGE);

  const variables = useMemo(
    () => ({
      page,
      limit: DEFAULT_LIMIT,
      warehouseLocationId: isMobileLocation ? undefined : locationId,
      mobileWarehouseLocationId: isMobileLocation ? locationId : undefined,
    }),
    [isMobileLocation, locationId, page]
  );

  const { data, loading } = useQuery(WAREHOUSE_LOCATION_AUDITS_QUERY, {
    skip: !locationId,
    variables,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const audits = data?.warehouseLocationAudits?.collection || [];
  const metadata = data?.warehouseLocationAudits?.metadata || {
    currentPage: page,
    totalPages: 0,
    totalCount: 0,
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      sx={{ "& .MuiDialog-paper": { width: "1200px", maxWidth: "90vw" } }}
    >
      <DialogTitle>
        <Typography fontWeight={500} fontSize={22} color="textPrimary" align="center">
          Audit Details
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <AuditDetailsModalSkeleton />
        ) : audits.length > 0 ? (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ width: "140px" }}>
                      Started At
                    </TableCell>
                    <TableCell align="center" sx={{ width: "140px" }}>
                      Completed At
                    </TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Audited By</TableCell>
                    <TableCell align="center">Duration</TableCell>
                    <TableCell align="center">Items</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audits.map(audit => (
                    <TableRow key={audit.id}>
                      <TableCell align="center" sx={{ width: "140px" }}>
                        {formatDateTime(audit.startedAt)}
                      </TableCell>
                      <TableCell align="center" sx={{ width: "140px" }}>
                        {formatDateTime(audit.completedAt)}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{audit.status || "-"}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {audit.auditedBy?.name || audit.auditedBy?.email || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {formatDuration(audit.durationSeconds)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{audit.auditItems?.length || 0}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {metadata.totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={metadata.totalPages}
                  page={metadata.currentPage}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        ) : (
          <Box p={3} textAlign="center">
            <Typography variant="body1">No audits yet</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
